import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { CoinIcon } from '@/features/shop/CoinIcon'
import { fetchQuests, type QuestDay, type QuestScope, replaceQuest } from './api'

export const questsKey = ['quests'] as const

const SCOPE: Record<QuestScope, string> = {
  people: 'с людьми',
  computer: 'с компьютером',
  any: '',
}

type Props = {
  /** Вошёл ли человек: гостю заданий не выдают — прогресс некуда записать. */
  signedIn: boolean
  onSignIn: () => void
  /** В магазин — тратить заработанное (features/shop). */
  onOpenShop: () => void
}

/**
 * Задания дня — под меню главного экрана (docs/QUESTS.md).
 *
 * Свёрнуты в одну строку: главный экран борется за вертикаль, и начать
 * партию человек должен, не прокручивая. Строка отвечает на вопрос «есть ли
 * что делать» — сколько выполнено, серия, монеты, — а сами задания
 * раскрываются нажатием.
 *
 * Гостю — та же строка с приглашением войти. Это не заглушка: задания —
 * главный повод завести учётную запись, и гость должен видеть, что теряет.
 */
export function QuestCard({ signedIn, onSignIn, onOpenShop }: Props) {
  if (!signedIn) {
    return (
      <div className="card quests">
        <div className="quests__head">
          <span className="quests__title">Задания дня</span>
          {/* Не просто «войти»: такая кнопка уже есть в шапке, а здесь
              важно, зачем. */}
          <button type="button" className="ghost" onClick={onSignIn}>
            войти за наградами
          </button>
        </div>
        <p className="muted">
          Выполняйте задания в партиях с людьми и с компьютером и получайте монеты.
        </p>
      </div>
    )
  }

  return <SignedInQuests onOpenShop={onOpenShop} />
}

function SignedInQuests({ onOpenShop }: { onOpenShop: () => void }) {
  const client = useQueryClient()
  const [open, setOpen] = useState(false)

  /*
   * Задания засчитывает сервер по концу партии, джобой, — вернувшийся на
   * главный экран должен увидеть свежий прогресс, поэтому запрос
   * повторяется при каждом показе.
   */
  const quests = useQuery({ queryKey: questsKey, queryFn: fetchQuests, staleTime: 0 })

  const replace = useMutation({
    mutationFn: replaceQuest,
    onSuccess: (day) => client.setQueryData<QuestDay>(questsKey, day),
  })

  // Пока не пришло или не удалось — ничего: меню важнее, и пустая рамка
  // на месте заданий только сдвигала бы его.
  if (quests.data === undefined) return null

  const { quests: list, streak, coins } = quests.data
  const done = list.filter((quest) => quest.done).length

  return (
    <div className="card quests">
      <button
        type="button"
        className="quests__head quests__toggle"
        aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
      >
        <span className="quests__title">Задания дня</span>
        <span className="quests__summary">
          <span>
            {done} из {list.length}
          </span>
          {streak > 0 && <span title="дней подряд">серия {streak}</span>}
          <span className="quests__coins">
            <span className="sr-only">монет:</span>
            {coins}
            <CoinIcon />
          </span>
        </span>
      </button>

      {open && (
        <ul className="quests__list">
          {list.map((quest) => (
            <li key={quest.id} className={quest.done ? 'quest quest--done' : 'quest'}>
              <span className="quest__title">{quest.title}</span>
              <span className="quest__reward">
                +{quest.reward}
                <CoinIcon />
              </span>
              <span className="quest__bar" aria-hidden="true">
                <span
                  className="quest__fill"
                  style={{ width: `${Math.round((quest.progress / quest.target) * 100)}%` }}
                />
              </span>
              {/* Счёт и замена — одной строкой во всю ширину, а не в колонках
                  названия и награды: «заменить» иначе расширяло колонку
                  награды, и на телефоне названия переносились раньше времени. */}
              <span className="quest__foot">
                <span className="quest__count">
                  {quest.done ? 'выполнено' : `${quest.progress} из ${quest.target}`}
                </span>
                {/* Где засчитывается — чтобы не играть с компьютером ради
                    задания, которое он не двигает. «Везде» не подписано:
                    это обычный случай. */}
                {!quest.done && (quest.scope ?? 'people') !== 'any' && (
                  <span className="quest__scope">{SCOPE[quest.scope ?? 'people']}</span>
                )}
                {quest.replaceable && (
                  <button
                    type="button"
                    className="ghost quest__replace"
                    disabled={replace.isPending}
                    onClick={() => replace.mutate(quest.id)}
                  >
                    заменить
                  </button>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}

      {open && (
        <p className="muted">
          Засчитываются партии, доигранные до победы: с людьми — если за столом хотя бы двое людей,
          с компьютером — если вы за столом один. Партии за одним устройством не считаются. Одно
          задание в день можно заменить.
        </p>
      )}

      {/* Монеты — ради того, на что их тратят: путь в магазин прямо здесь,
          рядом с балансом. */}
      {open && (
        <button type="button" className="ghost quests__shop" onClick={onOpenShop}>
          в магазин →
        </button>
      )}

      {replace.isError && <span className="banner banner--error">не удалось заменить</span>}
    </div>
  )
}
